import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthorityContext = createContext({
  authorities: [],
  defaultAuthorities: [],
  customAuthorities: [],
  loading: false,
  requiresOrgSelection: false,
  fetchAuthorities: () => Promise.resolve(),
  addAuthority: () => Promise.resolve(),
  updateAuthority: () => Promise.resolve(),
  deleteAuthority: () => Promise.resolve(),
});

export const useAuthority = () => useContext(AuthorityContext);

export const AuthorityProvider = ({ children }) => {
  const [defaultAuthorities, setDefaultAuthorities] = useState([]);
  const [customAuthorities, setCustomAuthorities] = useState([]);
  const [loading, setLoading] = useState(false);
  // True when the backend reports there is no single org to read (SuperAdmin in
  // the all-orgs state). The Settings tab shows an org-selection prompt instead.
  const [requiresOrgSelection, setRequiresOrgSelection] = useState(false);

  const getToken = () => {
    try {
      const raw = localStorage.getItem('token');
      return raw ? raw.replace(/^"|"$/g, '') : null;
    } catch { return null; }
  };

  const fetchAuthorities = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_BASE_URL}authority/get`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDefaultAuthorities(res.data?.result?.default_authorities || []);
      setCustomAuthorities(res.data?.result?.custom_authorities || []);
      setRequiresOrgSelection(!!res.data?.requires_org_selection);
    } catch (err) {
      console.warn('Failed to fetch authorities:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const addAuthority = useCallback(async (name) => {
    const token = getToken();
    if (!token) return;
    try {
      setLoading(true);
      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}authority/add`,
        { name },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      setDefaultAuthorities(res.data?.result?.default_authorities || []);
      setCustomAuthorities(res.data?.result?.custom_authorities || []);
    } catch (err) {
      console.error('Failed to add authority:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateAuthority = useCallback(async (originalName, updates = {}) => {
    const token = getToken();
    if (!token) return;
    try {
      setLoading(true);
      const res = await axios.put(
        `${process.env.REACT_APP_BASE_URL}authority/update`,
        {
          original_name: originalName,
          new_name:  updates.name,
          new_color: updates.color,
        },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      setDefaultAuthorities(res.data?.result?.default_authorities || []);
      setCustomAuthorities(res.data?.result?.custom_authorities || []);
    } catch (err) {
      console.error('Failed to update authority:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteAuthority = useCallback(async (name) => {
    const token = getToken();
    if (!token) return;
    try {
      setLoading(true);
      const res = await axios.delete(
        `${process.env.REACT_APP_BASE_URL}authority/delete`,
        {
          data: { name },
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        }
      );
      setDefaultAuthorities(res.data?.result?.default_authorities || []);
      setCustomAuthorities(res.data?.result?.custom_authorities || []);
    } catch (err) {
      console.error('Failed to delete authority:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // All authority names combined (for dropdowns)
  const authorities = [...defaultAuthorities.map(a => a.name), ...customAuthorities.map(a => a.name)];

  useEffect(() => {
    const token = getToken();
    if (token) fetchAuthorities();
  }, [fetchAuthorities]);

  return (
    <AuthorityContext.Provider value={{
      authorities,
      defaultAuthorities,
      customAuthorities,
      loading,
      requiresOrgSelection,
      fetchAuthorities,
      addAuthority,
      updateAuthority,
      deleteAuthority,
    }}>
      {children}
    </AuthorityContext.Provider>
  );
};

export default AuthorityContext;
